import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Segment} from '../../segment.model';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {SegmentListConfig} from '../../segment-config.model';
import {SelectionModel} from '@angular/cdk/collections';
import {BehaviorSubject, merge} from 'rxjs';
import {SegmentSearchFilterModel} from '../search/search-filter.model';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {SubSink} from 'subsink';
import {SegmentFacadeService} from '../../segment-facade.service';
import {ActivatedRoute} from '@angular/router';
import {filter, map} from 'rxjs/operators';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {flattenToSegments, SegmentsListFilterPage} from '../../segments.state';
import {ApiVersion} from '../../../app.types';

interface SegmentFlatNode extends Segment {
  expandable: boolean
  level: number
}

@Component({
  selector: 'app-tree-table',
  template: `
    <div class="table-handling">
      <app-hierarchical-table-custom-configuration></app-hierarchical-table-custom-configuration>
      <mat-paginator [pageSizeOptions]="[1,5,10,50,100]"
                     [length]="totalElements"
                     [pageIndex]="defaultPageIndex" [pageSize]="defaultPageSize"></mat-paginator>
    </div>
    <mat-table
      id="tree-table-segment-list"
      data-cy="tree-element-segment-list"
      [dataSource]="dataSource"
      matSort
      matSortActive="id"
      matSortDirection="desc">

      <ng-container matColumnDef="select">
        <mat-header-cell *matHeaderCellDef>
          <mat-checkbox (change)="$event ? selectAll() : null"
                        [checked]="multiSelection.hasValue() && isAllSelected()"
                        [indeterminate]="multiSelection.hasValue() && !isAllSelected()">
          </mat-checkbox>
        </mat-header-cell>
        <mat-cell *matCellDef="let segment">
          <mat-checkbox (click)="$event.stopPropagation()"
                        (change)="onCheckboxSelectChange($event, segment)"
                        [checked]="multiSelection.isSelected(segment.id)"
                        [disabled]="segment.entityType === 'TAXONOMY'"
          >
          </mat-checkbox>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="id">
        <mat-header-cell *matHeaderCellDef mat-sort-header="id" sortActionDescription="Sort by id"> ID
        </mat-header-cell>
        <mat-cell *matCellDef="let segment">
          <button mat-icon-button
                  [style.visibility]="!segment.expandable ? 'hidden' : ''"
                  [style.marginLeft.px]="(segment.level -1 ) * 16"
                  (click)="onExpandClick(segment, $event)">
            <mat-icon class="mat-icon-rtl-mirror">
              {{ treeControl.isExpanded(segment) ? 'expand_more' : 'chevron_right' }}
            </mat-icon>
          </button>
          {{ segment.id }}
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="mainType">
        <mat-header-cell *matHeaderCellDef> Main type
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.mainType }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="subType">
        <mat-header-cell *matHeaderCellDef> Sub type
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.subType }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="audienceType">
        <mat-header-cell *matHeaderCellDef> Audience type
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.audienceType }}</mat-cell>
      </ng-container>
      <ng-container matColumnDef="category">
        <mat-header-cell *matHeaderCellDef> Category
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.category }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="clientSideId">
        <mat-header-cell *matHeaderCellDef> eID
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.clientSideId }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="name">
        <mat-header-cell *matHeaderCellDef> Name
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"
                  [attr.data-cy]="'tree-element-segment-list-cell-name-' + segment.level"> {{ segment.name }}
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="partnerId">
        <mat-header-cell *matHeaderCellDef>
          Partner Id
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.partnerId }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="reachXpredict">
        <mat-header-cell *matHeaderCellDef mat-sort-header="reach.xpredict"
                         sortActionDescription="Sort by xpredict reach">Xpredict reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.reach?.xpredict | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="reach">
        <mat-header-cell *matHeaderCellDef mat-sort-header="reach.merged"
                         sortActionDescription="Sort by merged reach">
          Merged reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.reach?.merged | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="reachNarthaPure">
        <mat-header-cell *matHeaderCellDef mat-sort-header="reach.narthaPure"
                         sortActionDescription="Sort by nartha pure reach">Nartha reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.reach?.narthaPure | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="reachNarthaConverted">
        <mat-header-cell *matHeaderCellDef mat-sort-header="reach.narthaConverted"
                         sortActionDescription="Sort by xpredict reach">Nartha converted reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.reach?.narthaConverted | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="xandr">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.xandr"
                         sortActionDescription="Sort by xandr reach">Xandr reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.xandr | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="googleDbmEmetriqAdvertiser">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.googleDbmEmetriqAdvertiser"
                         sortActionDescription="Sort by Google DBM Emetriq Advertiser reach">Google DBM Emetriq
          reach
        </mat-header-cell>
        <mat-cell
          *matCellDef="let segment"> {{ segment.platformReach.googleDbmEmetriqAdvertiser | number }}
        </mat-cell>
      </ng-container>


      <ng-container matColumnDef="googleDbm">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.googleDbm"
                         sortActionDescription="Sort by Google DBM reach">Google DBM reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.googleDbm | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="googleDfp">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.googleDfp"
                         sortActionDescription="Sort by Google DFP reach">Google DFP reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.googleDfp | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="googleDdp">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.googleDdp"
                         sortActionDescription="Sort by Google DDP reach">Google DDP reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.googleDdp | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="adform">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.adform"
                         sortActionDescription="Sort by Adform reach">Adform reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.adform | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="adobe">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.adobe"
                         sortActionDescription="Sort by Adobe reach">Adobe reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.adobe | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="activeAgentTargetVideo">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentTargetVideo"
                         sortActionDescription="Sort by Active Agent Target Video reach">Active Agent Target Video
          reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentTargetVideo | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="activeAgentIqd">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentIqd"
                         sortActionDescription="Sort by Active Agent Iqd reach">Active Agent Iqd reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentIqd | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="activeAgentNetpoint">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentNetpoint"
                         sortActionDescription="Sort by Active Agent Netpoint reach">Active Agent Netpoint reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentNetpoint | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="activeAgentQuarterMedia">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentQuarterMedia"
                         sortActionDescription="Sort by Active Agent Quarter Media reach">Active Agent Quarter
          Media reach
        </mat-header-cell>
        <mat-cell
          *matCellDef="let segment"> {{ segment.platformReach.activeAgentQuarterMedia | number }}
        </mat-cell>
      </ng-container>


      <ng-container matColumnDef="activeAgentRnd">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentRnd"
                         sortActionDescription="Sort by Active Agent Rnd reach">Active Agent Rnd reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentRnd | number }}</mat-cell>
      </ng-container>


      <ng-container matColumnDef="activeAgentBcn">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentBcn"
                         sortActionDescription="Sort by Active Agent Bcn reach">Active Agent Bcn reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentBcn | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="activeAgentEmetriq">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.activeAgentEmetriq"
                         sortActionDescription="Sort by Active Agent Emetriq reach">Active Agent Emetriq reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.activeAgentEmetriq | number }}</mat-cell>
      </ng-container>
      <ng-container matColumnDef="yoc">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.yoc"
                         sortActionDescription="Sort by Yoc reach">Yoc reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.yoc | number }}</mat-cell>
      </ng-container>
      <ng-container matColumnDef="ttd">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.ttd"
                         sortActionDescription="Sort by TTD reach">TTD reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.ttd | number }}</mat-cell>
      </ng-container>
      <ng-container matColumnDef="pubmatic">
        <mat-header-cell *matHeaderCellDef mat-sort-header="platformReach.pubmatic"
                         sortActionDescription="Sort by Pubmatic reach">Pubmatic reach
        </mat-header-cell>
        <mat-cell *matCellDef="let segment"> {{ segment.platformReach.pubmatic | number }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="sharedAccounts">
        <mat-header-cell *matHeaderCellDef> Shared accounts
        </mat-header-cell>
        <mat-cell *matCellDef="let segment">
          <app-shared-accounts-row [sharedAccountsIds]="segment.sharedAccounts"></app-shared-accounts-row>
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="columnsToPresent"></mat-header-row>
      <mat-row *matRowDef="let segment; columns: columnsToPresent;"
               (click)="this.onSelect($event, segment.id);"
               [ngClass]="{'selected': isSegmentSelected(segment.id), 'special': !segment.partnerId}">
      </mat-row>
    </mat-table>
  `,
  styleUrls: ['../table/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreeTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @Output() selectedSegmentIds = new EventEmitter<number[]>();
  columnsToPresent: string[] = [];
  configCopy!: SegmentListConfig;
  multiSelection: SelectionModel<number> = new SelectionModel<number>(true, []);
  filter$: BehaviorSubject<SegmentSearchFilterModel> = new BehaviorSubject<SegmentSearchFilterModel>({});
  totalElements: number | undefined;
  treeControl = new FlatTreeControl<SegmentFlatNode, number>(
    node => node.level,
    node => node.expandable,
    {trackBy: dataNode => dataNode.id});
  readonly defaultPageIndex: number = 0;
  readonly defaultPageSize: number = 50;
  treeFlattener = new MatTreeFlattener<Segment, SegmentFlatNode, number>(
    (node, level) => ({
      ...node,
      expandable: node.hasChildren,
      level: node.level || level
    }),
    node => node.level,
    node => node.expandable,
    node => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  private subSink = new SubSink();

  constructor(private segmentsFacade: SegmentFacadeService,
              private activatedRoute: ActivatedRoute) {

  }

  @Input()
  set searchFormValues(val: SegmentSearchFilterModel) {
    this.filter$.next(val);
  }

  @Input() set config(config: SegmentListConfig) {
    this.configCopy = config;
    if (!this.columnsToPresent || this.columnsToPresent.length === 0) {
      this.columnsToPresent = [...config.tableColumnsToPresent, ...config.defaultTableColumns];
    }
  }

  @Input() set columnConfig(columns: string[] | null) {
    if (columns && columns.length !== 0) {
      this.columnsToPresent = [...new Set([...this.configCopy.tableColumnsToPresent, ...columns]).values()];
    }
  }

  ngOnInit(): void {
    this.listenToPageSortFilterChanges();
    this.populateDataSource();
    this.subSink.sink = this.activatedRoute.paramMap.pipe(
      filter((params) => params.get('id') !== undefined),
      map(params => params.get('id'))
    )
      .subscribe((id: string | null) => {
        if (id) {
          this.multiSelection.select(parseInt(id, 10));
        }
      });

    this.subSink.sink = this.multiSelection.changed.subscribe(() => {
      this.selectedSegmentIds.emit(this.multiSelection.selected);
    });
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  selectAll() {
    if (this.isAllSelected()) {
      this.multiSelection.clear();
      return;
    }
    const ids = this.getAllIdsFromDataSource();
    this.multiSelection.select(...ids);
  }

  onCheckboxSelectChange($event: MatCheckboxChange, segment: SegmentFlatNode) {
    this.multiSelection.toggle(segment.id);
  }

  clearSelection() {
    this.multiSelection.clear();
  }

  isAllSelected() {
    return this.multiSelection.selected.length === this.getAllIdsFromDataSource().length;
  }

  isSegmentSelected(segmentId: number) {
    return this.multiSelection.isSelected(segmentId);
  }

  onSelect($event: MouseEvent, segmentId: number) {
    if (this.getAllIdsFromDataSource().includes(segmentId)) {
      this.select(segmentId, $event.shiftKey);
    }
  }

  select(segmentId: number, multiSelectClick?: boolean) {
    if (this.multiSelection.isSelected(segmentId)) {
      this.multiSelection.toggle(segmentId);
    } else {
      if (multiSelectClick) {
        // unselect all selected text due to shift click
        // because css user-select: none would heavily impact UX
        document.getSelection()?.removeAllRanges();
      } else {
        this.multiSelection.clear();
      }
      this.multiSelection.select(segmentId);
    }
  }

  onExpandClick(segment: SegmentFlatNode, $event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.treeControl.toggle(segment);
  }

  private getAllIdsFromDataSource() {
    return this.reduceRecurrentIds(this.dataSource.data);
  }

  private reduceRecurrentIds(segments?: Segment[]): number[] {
    return flattenToSegments(segments || [])
      .map((it: Segment) => it.id);
  }

  private populateDataSource() {
    this.subSink.sink = this.segmentsFacade.getSegments().subscribe(segments => {
      this.dataSource.data = segments.content;
      this.totalElements = segments.totalElements;
    });
  }

  private listenToPageSortFilterChanges() {
    this.subSink.sink = merge(
      this.segmentsFacade.reloadSegments(),
      this.paginator.page,
      this.sort.sortChange,
      this.filter$.asObservable())
      .subscribe((value) => {
        if (value !== 'reload') {
          this.multiSelection.clear();
        }
        this.segmentsFacade.loadSegments(this.filter$.getValue(),
          {}, {
            page: this.paginator.pageIndex || this.defaultPageIndex,
            size: this.paginator.pageSize || this.defaultPageSize,
            sort:
              this.sort.active && this.sort.direction ? {
                sortField: this.sort.active,
                direction: this.sort.direction.toUpperCase()
              } : undefined
          } as SegmentsListFilterPage,
          ApiVersion.V2);
      });
  }

}
